<template>
  <transition name="cart-slide">
    <div class="mobile-cart-overlay" @click.self="$emit('close')">
      <div class="mobile-cart-view">
        <!-- Header -->
        <div class="cart-header">
          <div class="swipe-indicator"></div>
          <div class="header-content">
            <h2 class="cart-title">Cart ({{ cartItems.length }})</h2>
            <button class="close-btn" @click="$emit('close')">
              <font-awesome-icon icon="fa-solid fa-times" />
            </button>
          </div>
        </div>

        <!-- Customer Section -->
        <div class="customer-section" v-if="customer || true">
          <div class="customer-card" @click="customer ? unselectCustomer() : selectCustomer()">
            <div class="customer-icon">
              <font-awesome-icon :icon="customer ? 'fa-solid fa-user-check' : 'fa-solid fa-user-plus'" />
            </div>
            <div class="customer-info" v-if="customer">
              <h5 class="customer-name">{{ customer.Name }} {{ customer.Surname }}</h5>
              <p class="customer-details" v-if="customer.CellNumber">{{ customer.CellNumber }}</p>
              <p class="customer-details" v-if="customer.vat">TPIN: {{ customer.vat }}</p>
            </div>
            <div class="customer-info" v-else>
              <h5 class="customer-name">Select Customer</h5>
              <p class="customer-details">Tap to add customer</p>
            </div>
            <font-awesome-icon 
              :icon="customer ? 'fa-solid fa-times-circle' : 'fa-solid fa-chevron-right'" 
              class="customer-action-icon"
            />
          </div>
        </div>

        <!-- Cart Items -->
        <div class="cart-items-container">
          <div v-if="cartItems.length === 0" class="empty-cart">
            <font-awesome-icon icon="fa-solid fa-shopping-cart" class="empty-icon" />
            <p class="empty-text">Your cart is empty</p>
            <p class="empty-subtext">Add items to get started</p>
          </div>

          <div v-else class="cart-items-list">
            <div 
              v-for="(item, index) in cartItems" 
              :key="index"
              class="cart-item"
            >
              <div class="item-info">
                <h4 class="item-name">{{ item.Description }}</h4>
                <p class="item-price">{{ formatPrice(item.Unit) }} each</p>
              </div>
              
              <div class="item-controls">
                <div class="quantity-controls">
                  <button 
                    class="qty-btn" 
                    @click="decrementQuantity(index)"
                  >
                    <font-awesome-icon icon="fa-solid fa-minus" />
                  </button>
                  <span class="quantity">{{ item.Qty }}</span>
                  <button 
                    class="qty-btn" 
                    @click="incrementQuantity(index)"
                  >
                    <font-awesome-icon icon="fa-solid fa-plus" />
                  </button>
                </div>
                
                <div class="item-total">
                  <span class="total-label">Total:</span>
                  <span class="total-amount">{{ formatPrice(item.Line) }}</span>
                </div>
                
                <button 
                  class="delete-btn" 
                  @click="removeItem(index)"
                >
                  <font-awesome-icon icon="fa-solid fa-trash" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Cart Summary -->
        <div class="cart-summary" v-if="cartItems.length > 0">
          <div class="summary-row">
            <span class="summary-label">Subtotal:</span>
            <span class="summary-value">{{ formatPrice(subtotal) }}</span>
          </div>
          
          <div class="summary-row" v-if="tipAmount > 0">
            <span class="summary-label">Tip/Service:</span>
            <span class="summary-value">{{ formatPrice(tipAmount) }}</span>
          </div>
          
          <div class="summary-row" v-if="discount > 0">
            <span class="summary-label">Discount:</span>
            <span class="summary-value danger">-{{ formatPrice(discount) }}</span>
          </div>
          
          <div class="summary-row total-row">
            <span class="summary-label">Total:</span>
            <span class="summary-value total">{{ formatPrice(total) }}</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="cart-actions" v-if="cartItems.length > 0">
          <button class="action-btn secondary" @click="clearCart">
            <font-awesome-icon icon="fa-solid fa-times-circle" />
            <span>Clear Cart</span>
          </button>
          
          <button class="action-btn secondary" @click="$emit('save-table')">
            <font-awesome-icon icon="fa-solid fa-save" />
            <span>Save Table</span>
          </button>
          
          <button class="action-btn primary" @click="$emit('proceed-to-payment')">
            <font-awesome-icon icon="fa-solid fa-credit-card" />
            <span>Proceed to Pay</span>
          </button>
        </div>

        <!-- Offline Create Customer Modal -->
        <custom-modal ref="offlineCustomerModal" title="Create Customer (Offline)" :is-form="true" @submit="submitOfflineCustomer">
          <div class="form-floating mb-2">
            <input type="text" class="form-control" id="offline_name" v-model="offlineCustomerForm.Name" placeholder="Customer Name" required>
            <label for="offline_name">Customer Name</label>
          </div>
          <div class="form-floating mb-2">
            <input type="tel" class="form-control" id="offline_contact" v-model="offlineCustomerForm.CellNumber" placeholder="Contact No." required>
            <label for="offline_contact">Contact No.</label>
          </div>
          <div class="form-floating mb-2">
            <input type="text" class="form-control" id="offline_surname" v-model="offlineCustomerForm.Surname" placeholder="Surname (optional)">
            <label for="offline_surname">Surname (optional)</label>
          </div>
          <div class="form-floating mb-2">
            <input type="text" class="form-control" id="offline_address" v-model="offlineCustomerForm.Adress1" placeholder="Address (optional)">
            <label for="offline_address">Address (optional)</label>
          </div>
        </custom-modal>
      </div>
    </div>
  </transition>
</template>

<script>
import CustomModal from "@/components/layout/utils/CustomModal";
import connectivityMonitor from "@/services/connectivityMonitor";
import { responses } from "@/utils/responses";
import { errorHandler } from "@/services/errorHandler";

export default {
  name: 'MobileCartView',
  components: { CustomModal },
  data() {
    return {
      isOnline: true,
      unsubscribeOnline: null,
      unsubscribeOffline: null,
      offlineCustomerForm: { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' },
    };
  },
  mounted() {
    // Initialize online status
    this.isOnline = connectivityMonitor.getStatus();
    // Subscribe to connectivity events
    this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
      this.isOnline = true;
    });
    this.unsubscribeOffline = connectivityMonitor.subscribe('offline', () => {
      this.isOnline = false;
    });
  },
  beforeDestroy() {
    if (this.unsubscribeOnline) this.unsubscribeOnline();
    if (this.unsubscribeOffline) this.unsubscribeOffline();
  },
  methods: {
    formatPrice(price) {
      return this.$utils.formatMoney(price);
    },
    incrementQuantity(index) {
      const item = this.cartItems[index];
      const newQty = item.Qty + 1;
      this.$store.commit('updateSaleLineQty', { saleLine: item, qty: newQty });
      this.$store.dispatch('calculateTipAmount');
    },
    decrementQuantity(index) {
      const item = this.cartItems[index];
      if (item.Qty <= 1) {
        this.removeItem(index);
      } else {
        const newQty = item.Qty - 1;
        this.$store.commit('updateSaleLineQty', { saleLine: item, qty: newQty });
        this.$store.dispatch('calculateTipAmount');
      }
    },
    removeItem(index) {
      const item = this.cartItems[index];
      this.$store.commit('setOverrideDetails', `Void Line (${item.Description})`);
      this.$store.commit('voidLine', item);
    },
    async clearCart() {
      const result = await Swal.fire({
        title: 'Clear Cart?',
        text: 'Are you sure you want to clear all items from the cart?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Clear',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
        this.$store.commit('clearSale');
        this.$emit('close');
      }
    },
    selectCustomer() {
      if (this.isOnline) {
        return this.$router.push({ name: 'select-customer' });
      }
      if (this.$refs.offlineCustomerModal) {
        this.offlineCustomerForm = { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' };
        this.$refs.offlineCustomerModal.openModal();
      }
    },
    async submitOfflineCustomer() {
      try {
        if (!this.offlineCustomerForm?.Name || !this.offlineCustomerForm?.CellNumber) {
          responses.showWarning('Please provide Name and Contact');
          return;
        }
        this.$utils.showLoading();
        const rec = await this.$store.dispatch('createOfflineDebtor', this.offlineCustomerForm);
        if (rec && rec.Id) {
          this.$store.commit('setCustomer', rec.Id);
          responses.showSuccess('Offline customer added to sale');
          if (this.$refs.offlineCustomerModal) this.$refs.offlineCustomerModal.closeModal();
        }
      } catch (e) {
        errorHandler.tomcatError(e, false, 'Failed to save offline customer');
      } finally {
        this.$utils.hideLoading();
      }
    },
    unselectCustomer() {
      this.$store.commit('setCustomer', '');
    }
  },
  computed: {
    customer() {
      let id = this.$store.getters.getCustomer;
      return this.$store.getters.getDebtorByID(id);
    },
    cartItems() {
      return this.$store.getters.getSaleLines || [];
    },
    subtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.Line || 0), 0);
    },
    tipAmount() {
      return this.$store.getters.getTipAmount || 0;
    },
    discount() {
      return parseFloat(this.$store.getters.getSaleDiscount?.total || 0);
    },
    total() {
      return this.subtotal + this.tipAmount - this.discount;
    }
  }
};
</script>

<style scoped lang="scss">
.mobile-cart-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
}

.mobile-cart-view {
  width: 100%;
  max-height: 90vh;
  background: var(--card-bg);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

.cart-header {
  position: relative;
  padding: 12px 16px 16px;
  border-bottom: var(--card-border);
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.08) 0%, rgba(var(--accent-neon-purple-rgb), 0.08) 100%);
  
  .swipe-indicator {
    width: 40px;
    height: 4px;
    background: var(--text-secondary);
    border-radius: 2px;
    margin: 0 auto 12px;
    opacity: 0.5;
  }
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .cart-title {
      font-size: 20px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0;
    }
    
    .close-btn {
      width: 40px;
      height: 40px;
      border-radius: var(--button-radius);
      background: transparent;
      color: var(--text-secondary);
      border: var(--card-border);
      font-size: 18px;
      cursor: pointer;
      transition: var(--transition-default);
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:active {
        transform: scale(0.95);
        background: var(--secondary-button-bg);
      }
    }
  }
}

.cart-items-container {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  
  .empty-icon {
    font-size: 64px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  .empty-text {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .empty-subtext {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }
}

.cart-items-list {
  padding: 8px;
}

.cart-item {
  background: var(--bg-primary);
  border-radius: var(--card-radius);
  border: var(--card-border);
  padding: 16px;
  margin-bottom: 8px;
  box-shadow: var(--card-shadow);
  
  .item-info {
    margin-bottom: 12px;
    
    .item-name {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 4px 0;
    }
    
    .item-price {
      font-size: 14px;
      color: var(--text-secondary);
      margin: 0;
    }
  }
  
  .item-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .quantity-controls {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .qty-btn {
        width: 36px;
        height: 36px;
        border-radius: var(--button-radius);
        background: var(--accent-primary);
        color: white;
        border: none;
        font-size: 14px;
        cursor: pointer;
        transition: var(--transition-default);
        display: flex;
        align-items: center;
        justify-content: center;
        
        &:active {
          transform: scale(0.9);
        }
      }
      
      .quantity {
        min-width: 40px;
        text-align: center;
        font-size: 16px;
        font-weight: 700;
        color: var(--text-primary);
      }
    }
    
    .item-total {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      
      .total-label {
        font-size: 12px;
        color: var(--text-secondary);
      }
      
      .total-amount {
        font-size: 18px;
        font-weight: 700;
        color: var(--accent-primary);
      }
    }
    
    .delete-btn {
      width: 40px;
      height: 40px;
      border-radius: var(--button-radius);
      background: transparent;
      color: var(--danger);
      border: 1px solid var(--danger);
      font-size: 16px;
      cursor: pointer;
      transition: var(--transition-default);
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:active {
        transform: scale(0.9);
        background: rgba(var(--danger-rgb), 0.1);
      }
    }
  }
}

.cart-summary {
  padding: 16px;
  border-top: var(--card-border);
  background: var(--bg-primary);
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    
    &.total-row {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 2px solid var(--accent-primary);
    }
    
    .summary-label {
      font-size: 14px;
      color: var(--text-secondary);
      
      .total-row & {
        font-size: 18px;
        font-weight: 700;
        color: var(--text-primary);
      }
    }
    
    .summary-value {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
      
      &.danger {
        color: var(--danger);
      }
      
      &.total {
        font-size: 24px;
        font-weight: 700;
        color: var(--accent-primary);
      }
    }
  }
}

.cart-actions {
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  border-top: var(--card-border);
  background: var(--bg-primary);
  display: flex;
  gap: 8px;
  
  .action-btn {
    flex: 1;
    height: 56px;
    border-radius: var(--button-radius);
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: var(--transition-default);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    
    &.primary {
      background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.4);
      
      &:active {
        transform: scale(0.98);
        box-shadow: 0 2px 8px rgba(var(--accent-electric-blue-rgb), 0.3);
      }
    }
    
    &.secondary {
      background: var(--secondary-button-bg);
      color: var(--secondary-button-text);
      border: var(--card-border);
      
      &:active {
        transform: scale(0.98);
        background: var(--secondary-button-bg-dark);
      }
    }
  }
}

// Animations
.cart-slide-enter-active, .cart-slide-leave-active {
  transition: all 0.3s ease;
}

.cart-slide-enter, .cart-slide-leave-to {
  opacity: 0;
  
  .mobile-cart-view {
    transform: translateY(100%);
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

// Customer Section
.customer-section {
  padding: 12px 16px;
  border-bottom: var(--card-border);
  background: var(--bg-primary);
}

.customer-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.05) 0%, rgba(var(--accent-neon-purple-rgb), 0.05) 100%);
  border: var(--card-border);
  border-radius: var(--card-radius);
  cursor: pointer;
  transition: var(--transition-default);
  
  &:active {
    transform: scale(0.98);
    background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.1) 0%, rgba(var(--accent-neon-purple-rgb), 0.1) 100%);
  }
  
  .customer-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--accent-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  
  .customer-info {
    flex: 1;
    min-width: 0;
    
    .customer-name {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 4px 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .customer-details {
      font-size: 12px;
      color: var(--text-secondary);
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .customer-action-icon {
    font-size: 18px;
    color: var(--text-secondary);
    flex-shrink: 0;
  }
}
</style>

<style lang="scss">
// Dark mode styles for CustomModal (Offline Create Customer Modal)
.custom-modal {
  .modal-content {
    background: var(--card-bg) !important;
    border: var(--card-border) !important;
    border-radius: 12px !important;
  }

  .modal-header {
    background: var(--card-bg) !important;
    border-bottom: var(--card-border) !important;

    .modal-title {
      color: var(--text-primary) !important;
      font-weight: 600;
    }

    .btn-close {
      color: var(--text-primary) !important;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: var(--card-bg) !important;
  }

  .form-floating {
    margin-bottom: 12px;

    .form-control {
      background: var(--card-bg) !important;
      border: var(--card-border) !important;
      color: var(--text-primary) !important;
      border-radius: 8px !important;
      padding: 12px 16px !important;

      &::placeholder {
        color: var(--text-secondary) !important;
      }

      &:focus {
        border-color: var(--accent-electric-blue) !important;
        box-shadow: 0 0 0 0.2rem rgba(var(--accent-electric-blue-rgb), 0.15) !important;
        background: var(--card-bg) !important;
        color: var(--text-primary) !important;
      }
    }

    label {
      color: var(--text-secondary) !important;
      padding-left: 4px;
    }
  }
}
</style>

<style>
// Global styles for CustomModal (non-scoped)
.modal-backdrop {
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.custom-modal {
  .modal-content {
    background: var(--card-bg);
    border: var(--card-border);
    border-radius: 12px;
  }

  .modal-header {
    background: var(--card-bg);
    border-bottom: var(--card-border);

    .modal-title {
      color: var(--text-primary);
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: var(--card-bg);
  }
}
</style>
